import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SupabaseService } from './supabase.service';
import { ProfileDetails } from '../models/profile.interface';
import { Job } from '../types/job.types';
import { environment } from '@environments/environment';
import { salaryDollarDisambiguationSuffix } from '../utils/salary-range-format.util';

export interface CalibrationSignals {
  location?: CalibrationCategory;
  compensation?: CalibrationCategory;
  culture?: CalibrationCategory;
  role?: CalibrationCategory;
  skills?: CalibrationCategory;
  company_type?: CalibrationCategory;
}

export interface CalibrationCategory {
  priority: number;
  description: string;
  excellentFit: string[];
  goodFit: string[];
  borderline: string[];
  notAFit: string[];
}

interface EmbeddingResponse {
  embedding: number[];
  usage?: {
    prompt_tokens: number;
    total_tokens: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class EmbeddingService {
  private http = inject(HttpClient);
  private readonly MODEL = 'text-embedding-3-small';
  private readonly MAX_TEXT_LENGTH = 8000; // Token limit safety
  
  // Use existing Supabase Edge Function for embeddings
  private readonly EMBEDDING_URL = 
    'https://ykimklpimdbuwxceyngs.supabase.co/functions/v1/generate-embeddings';

  constructor(
    private supabaseService: SupabaseService
  ) {}

  /**
   * Get headers for API requests using Supabase auth
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${environment.supabaseKey}`,
    });
  }

  /**
   * Generate embedding for any text using OpenAI's embedding model via Supabase Edge Function
   */
  async generateEmbedding(text: string): Promise<number[]> {
    try {
      // Truncate text to avoid token limits
      const truncatedText = text.substring(0, this.MAX_TEXT_LENGTH);
      
      const response = await this.http.post<EmbeddingResponse>(
        this.EMBEDDING_URL,
        {
          text: truncatedText,
          model: this.MODEL,
        },
        { headers: this.getHeaders() }
      ).toPromise();

      if (!response?.embedding) {
        throw new Error('Invalid embedding response from API');
      }

      return response.embedding;
    } catch (error) {
      throw new Error(`Failed to generate embedding: ${error}`);
    }
  }

  /**
   * Generate embedding for a user's profile combining resume and calibration signals
   */
  async generateProfileEmbedding(
    profile: ProfileDetails, 
    signals?: CalibrationSignals
  ): Promise<number[]> {
    // Combine profile data with calibration signals for better context
    const profileText = this.buildProfileText(profile, signals);
    const embedding = await this.generateEmbedding(profileText);
    return embedding;
  }

  /**
   * Generate embedding for a job posting
   */
  async generateJobEmbedding(job: Job): Promise<number[]> {
    const jobText = this.buildJobText(job);
    return this.generateEmbedding(jobText);
  }

  /**
   * Get or create cached embedding for common queries
   * Note: Cache is optional - if it fails, we generate embedding directly
   */
  async getOrCreateQueryEmbedding(query: string): Promise<number[]> {
    // Try to check cache first (silently fail if cache table has issues)
    try {
      const { data: cached, error } = await this.supabaseService
        .getClient()
        .from('semantic_query_cache')
        .select('embedding')
        .eq('query_text', query)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle(); // Use maybeSingle to avoid errors when no row exists

      if (!error && cached?.embedding) {
        return JSON.parse(cached.embedding);
      }
    } catch {
      // Cache lookup failed - continue to generate embedding
    }

    // Generate new embedding
    const embedding = await this.generateEmbedding(query);

    // Try to cache it (silently fail if cache has RLS issues)
    try {
      await this.supabaseService
        .getClient()
        .from('semantic_query_cache')
        .upsert({
          query_text: query,
          embedding: JSON.stringify(embedding),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        });
    } catch {
      // Cache save failed - that's okay, embedding is already generated
    }

    return embedding;
  }

  /**
   * Save user's profile embedding to database
   */
  async saveUserEmbedding(userId: string, embedding: number[]): Promise<void> {
    try {
      // Store embedding as array (not JSON string) for vector type compatibility
      const { error } = await this.supabaseService
        .getClient()
        .from('users')
        .update({
          profile_embedding: embedding // Store as array, not JSON.stringify
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    } catch (error) {
      throw new Error(`Failed to save user embedding: ${error}`);
    }
  }

  /**
   * Save job embedding to database
   */
  async saveJobEmbedding(jobId: string, embedding: number[]): Promise<void> {
    try {
      // Store the vector in the dedicated job_embeddings table (kept out of
      // the hot `jobs` table for Disk IO). Store as array for vector type.
      const { error: embeddingError } = await this.supabaseService
        .getClient()
        .from('job_embeddings')
        .upsert({
          job_id: jobId,
          embedding: embedding,
          generated_at: new Date().toISOString(),
        });

      if (embeddingError) {
        throw embeddingError;
      }

      // Lightweight "already embedded" flag on jobs (timestamp only).
      const { error: flagError } = await this.supabaseService
        .getClient()
        .from('jobs')
        .update({ embedding_generated_at: new Date().toISOString() })
        .eq('id', jobId);

      if (flagError) {
        throw flagError;
      }
    } catch (error) {
      throw new Error(`Failed to save job embedding: ${error}`);
    }
  }

  /**
   * Get user's profile embedding from database
   */
  async getUserEmbedding(userId: string): Promise<number[] | null> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('users')
        .select('profile_embedding')
        .eq('id', userId)
        .single();

      if (error || !data?.profile_embedding) {
        return null;
      }

      // Handle both array and JSON string formats for backwards compatibility
      const embedding = data.profile_embedding;
      if (Array.isArray(embedding)) {
        return embedding;
      } else if (typeof embedding === 'string') {
        return JSON.parse(embedding);
      }
      return embedding;
    } catch {
      return null;
    }
  }

  /**
   * Clean up expired cache entries
   */
  async cleanupExpiredCache(): Promise<void> {
    try {
      await this.supabaseService
        .getClient()
        .rpc('cleanup_expired_cache');
    } catch {
    }
  }

  /**
   * Build comprehensive text for profile embedding
   */
  private buildProfileText(profile: ProfileDetails, signals?: CalibrationSignals): string {
    const parts: string[] = [];

    // Basic profile information
    if (profile.extractedText) {
      parts.push(`Resume: ${profile.extractedText}`);
    }

    if (profile.targetRoles?.length) {
      parts.push(`Target Roles: ${profile.targetRoles.join(', ')}`);
    }

    if (profile.seniority) {
      parts.push(`Seniority: ${profile.seniority}`);
    }

    if (profile.location) {
      parts.push(`Location Preference: ${profile.location}`);
    }

    if (profile.skills?.length) {
      parts.push(`Skills: ${profile.skills.join(', ')}`);
    }

    if (profile.industries?.length) {
      parts.push(`Industries: ${profile.industries.join(', ')}`);
    }

    if (profile.preferredWorkMode) {
      parts.push(`Work Mode: ${profile.preferredWorkMode}`);
    }

    // Add calibration signals for better matching
    if (signals) {
      Object.entries(signals).forEach(([category, categoryData]) => {
        if (categoryData.excellentFit.length) {
          parts.push(`Excellent ${category}: ${categoryData.excellentFit.join(', ')}`);
        }
        if (categoryData.goodFit.length) {
          parts.push(`Good ${category}: ${categoryData.goodFit.join(', ')}`);
        }
        if (categoryData.notAFit.length) {
          parts.push(`Avoid ${category}: ${categoryData.notAFit.join(', ')}`);
        }
      });
    }

    return parts.join('\n\n');
  }

  /**
   * Build comprehensive text for job embedding
   */
  private buildJobText(job: Job): string {
    const parts: string[] = [];

    if (job.title) {
      parts.push(`Title: ${job.title}`);
    }

    if (job.company) {
      parts.push(`Company: ${job.company}`);
    }

    if (job.level) {
      parts.push(`Level: ${job.level}`);
    }

    if (job.display_location || job.location) {
      parts.push(`Location: ${job.display_location || job.location}`);
    }

    if (job.work_type || job.employment_type) {
      parts.push(`Work Type: ${job.work_type || job.employment_type}`);
    }

    if (job.short_description) {
      parts.push(`Description: ${job.short_description}`);
    }

    if (job.responsibilities?.length) {
      parts.push(`Responsibilities: ${job.responsibilities.join(', ')}`);
    }

    if (job.requirements?.length) {
      parts.push(`Requirements: ${job.requirements.join(', ')}`);
    }

    if (job.skills?.length) {
      parts.push(`Skills: ${job.skills.join(', ')}`);
    }

    if (job.salary_min && job.salary_max) {
      parts.push(
        `Salary: ${job.salary_min} - ${job.salary_max}` +
          salaryDollarDisambiguationSuffix(job.salary_currency)
      );
    }

    return parts.join('\n\n');
  }

}